/**
 * One day of the "A · Columns" grid: header, divider, and that day's times.
 * Presentational; hover/focus fade state is owned by SlotGrid.
 */
import { FC, useEffect, useState } from "react"
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"

import { Text } from "@/components/Text"

import { webTransition } from "./motion"
import type { SlotColumn } from "./rescheduleSlotModel"
import { SlotButton } from "./SlotButton"
import { COLUMN_MAX_HEIGHT, font, tabular, tokens } from "./tokens"

export interface DayColumnProps {
  col: SlotColumn
  /** Not the first column: draws the divider to the previous day. */
  hasPrevious: boolean
  recommendedTime: string | null
  expanded: boolean
  /** Another column is hovered/focused: fade this one back. */
  dimmed: boolean
  /** This column is hovered/focused: weekday goes to full ink. */
  active: boolean
  onHoverIn: () => void
  onHoverOut: () => void
  onSlotFocus: () => void
  onSlotBlur: () => void
  onSelect: (date: string, time: string) => void
  onRetry: (date: string) => void
}

export const DayColumn: FC<DayColumnProps> = (props) => {
  const { col, hasPrevious, dimmed, active, onHoverIn, onHoverOut } = props
  return (
    <Pressable
      accessible={false}
      focusable={false}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      testID={`day-col-${col.date}`}
      style={[$column, col.weekBreak && $weekBreak, dimmed ? $dimmed : $visible, $fade]}
    >
      {col.weekBreak ? (
        <View testID={`week-break-${col.date}`} style={$weekDivider} pointerEvents="none" />
      ) : (
        hasPrevious && (
          <View testID={`day-divider-${col.date}`} style={$divider} pointerEvents="none" />
        )
      )}
      <View
        style={$header}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <Text style={[$weekday, active && $weekdayActive]}>{col.headerLabel}</Text>
        <Text style={$dayNumber}>{col.dayNumber}</Text>
        <Text style={$month}>{col.month}</Text>
      </View>
      <ColumnBody {...props} />
    </Pressable>
  )
}

const ColumnBody: FC<DayColumnProps> = ({
  col,
  recommendedTime,
  expanded,
  onSlotFocus,
  onSlotBlur,
  onSelect,
  onRetry,
}) => {
  if (col.status === "loading") return <Skeleton date={col.date} />
  if (col.status === "error") {
    return (
      <View style={$placeholder}>
        <Text style={$placeholderText}>Nicht ladbar</Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${col.headerLabel} ${col.dayNumber}. ${col.month} erneut laden`}
          onPress={() => onRetry(col.date)}
          testID={`day-retry-${col.date}`}
          hitSlop={8}
        >
          <Text style={$retry}>Erneut laden</Text>
        </Pressable>
      </View>
    )
  }
  if (col.status === "empty") {
    return (
      <View style={$placeholder}>
        <Text style={$placeholderText}>Ausgebucht</Text>
      </View>
    )
  }
  return (
    <SlotList col={col} capped={col.hiddenCount > 0 && !expanded}>
      {col.entries.map((entry) => (
        <SlotButton
          key={entry.time}
          time={entry.time}
          mine={entry.mine}
          recommended={!entry.mine && entry.time === recommendedTime}
          label={
            entry.mine
              ? `${entry.time} Uhr, Ihr aktueller Termin`
              : `${col.headerLabel} ${col.dayNumber}. ${col.month}, ${entry.time} Uhr`
          }
          onPress={() => onSelect(col.date, entry.time)}
          onFocus={onSlotFocus}
          onBlur={onSlotBlur}
        />
      ))}
    </SlotList>
  )
}

/** The times of a day; capped = 4 full slots + a peek of the 5th, scrolls on its own. */
const SlotList: FC<{ col: SlotColumn; capped: boolean; children: React.ReactNode }> = ({
  col,
  capped,
  children,
}) => {
  const [moreBelow, setMoreBelow] = useState(true)
  useEffect(() => setMoreBelow(true), [capped])

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent
    setMoreBelow(contentOffset.y + layoutMeasurement.height < contentSize.height - 4)
  }

  if (!capped) {
    return (
      <View style={$slotsWrap}>
        <View style={$slots}>{children}</View>
      </View>
    )
  }
  return (
    <View style={$slotsWrap}>
      <ScrollView
        testID={`day-scroll-${col.date}`}
        style={$scroll}
        contentContainerStyle={$slots}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={onScroll}
        nestedScrollEnabled
      >
        {children}
      </ScrollView>
      {moreBelow && (
        <View testID={`day-fade-${col.date}`} pointerEvents="none" style={$bottomFade} />
      )}
    </View>
  )
}

const Skeleton: FC<{ date: string }> = ({ date }) => (
  <View testID={`day-skeleton-${date}`} style={$skeleton}>
    {[0, 1, 2, 3].map((i) => (
      <View key={i} style={$skeletonBlock} />
    ))}
  </View>
)

const $column: ViewStyle = { flex: 1, minWidth: 0, gap: 8 }
const $fade: ViewStyle = webTransition("opacity", 180)
const $dimmed: ViewStyle = { opacity: 0.45 }
const $visible: ViewStyle = { opacity: 1 }
const $weekBreak: ViewStyle = { marginLeft: 10 }
const $divider: ViewStyle = {
  position: "absolute",
  left: -6.5,
  top: 6,
  bottom: 6,
  width: 1,
  backgroundColor: tokens.hairline,
}
const $weekDivider: ViewStyle = {
  position: "absolute",
  left: -11.5,
  top: 0,
  bottom: 0,
  width: 1.5,
  backgroundColor: tokens.field,
  opacity: 0.45,
}
const $header: ViewStyle = {
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 4,
  minHeight: 66,
  paddingTop: 4,
}
const $weekday: TextStyle = {
  fontSize: 13,
  lineHeight: 15,
  fontFamily: font.semi,
  color: tokens.ink70,
  ...webTransition("color", 180),
}
const $weekdayActive: TextStyle = { color: tokens.ink }
const $dayNumber: TextStyle = {
  fontSize: 28,
  lineHeight: 32,
  fontFamily: font.bold,
  letterSpacing: -0.56,
  color: tokens.ink,
  ...tabular,
}
const $month: TextStyle = {
  fontSize: 12,
  lineHeight: 14,
  fontFamily: font.semi,
  color: tokens.ink70,
}
// 10px top zone for the "Empfohlen" / "Ihr Termin" pill, pulled up into the header gap.
const $slotsWrap: ViewStyle = { marginTop: -10 }
const $scroll = { maxHeight: COLUMN_MAX_HEIGHT, overscrollBehavior: "contain" } as ViewStyle
const $slots: ViewStyle = { gap: 8, paddingTop: 10 }
const $bottomFade = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: 40,
  backgroundImage: "linear-gradient(rgba(255,255,255,0), #fff 85%)",
} as ViewStyle
const $placeholder: ViewStyle = {
  minHeight: 48,
  borderRadius: 12,
  borderWidth: 1,
  borderStyle: "dashed",
  borderColor: tokens.line,
  alignItems: "center",
  justifyContent: "center",
  padding: 4,
  gap: 2,
}
const $placeholderText: TextStyle = {
  fontSize: 12,
  fontFamily: font.semi,
  color: tokens.ink45,
  textAlign: "center",
}
const $retry: TextStyle = {
  fontSize: 12,
  fontFamily: font.bold,
  color: tokens.ink,
  textDecorationLine: "underline",
}
const $skeleton: ViewStyle = { gap: 8 }
const $skeletonBlock: ViewStyle = { height: 48, borderRadius: 12, backgroundColor: tokens.mist }
